import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useSelector } from 'react-redux';

import type { RootState } from '../../../../store/store';
import { JWTHeader } from '../../../../utils/JWTHeader';
import Popup from '../../../common/Popup/Popup.module';
import Spinner from '../../../common/Spinner';
import type { SCIPhysicalOwner } from './PhysicalOwnersTable';
import PhysicalOwnersTable, { ownerFirstnames } from './PhysicalOwnersTable';
import type { SCIQualifiedContact } from './SCIQualifiedContactsTable';

// Only the targets the user set in the modal: an untouched target is not sent
export type SCIContactTargets = Partial<Record<'targetedGestion' | 'targetedTransaction', boolean>>;

type TargetsState = Record<'targetedGestion' | 'targetedTransaction', boolean | null>;

interface SCIContactQualificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  sciName: string;
  siren?: string;
  initialContact?: SCIQualifiedContact | null;
  isSaving?: boolean;
  isLoading?: boolean;
  // Current targets of the SCI: when given, they can be set with the contact and the changed ones go to onSave
  initialTargets?: { targetedGestion?: boolean | null; targetedTransaction?: boolean | null } | null;
  // false keeps the modal open (save failed); anything else closes it
  onSave: (contact: SCIQualifiedContact, targets?: SCIContactTargets) => Promise<boolean | void> | boolean | void;
  onDelete?: () => Promise<void> | void;
}

const targetsFrom = (initial: SCIContactQualificationModalProps['initialTargets']): TargetsState => ({
  targetedGestion: initial?.targetedGestion ?? null,
  targetedTransaction: initial?.targetedTransaction ?? null,
});

interface TargetChoiceProps {
  label: string;
  value: boolean | null;
  disabled: boolean;
  onChange: (value: boolean) => void;
}

const TargetChoice: React.FC<TargetChoiceProps> = ({ label, value, disabled, onChange }) => (
  <div className="flex items-center justify-between gap-3" role="group" aria-label={label}>
    <span className="text-xs text-gray-700">
      {label}
      {value === null && <span className="ml-1 text-gray-400">(non renseigné)</span>}
    </span>
    <div className="flex overflow-hidden rounded border">
      {[true, false].map((option) => (
        <button
          key={String(option)}
          type="button"
          aria-pressed={value === option}
          disabled={disabled}
          onClick={() => onChange(option)}
          className={`px-3 py-1 text-xs disabled:opacity-50 ${value === option ? 'bg-rgpd-blue-dark text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
        >
          {option ? 'Oui' : 'Non'}
        </button>
      ))}
    </div>
  </div>
);

const SCIContactQualificationModal: React.FC<SCIContactQualificationModalProps> = ({
  isOpen,
  onClose,
  sciName,
  siren,
  initialContact,
  isSaving = false,
  isLoading = false,
  initialTargets,
  onSave,
  onDelete,
}) => {
  const [contactForm, setContactForm] = useState<SCIQualifiedContact>({});
  const [targetsForm, setTargetsForm] = useState<TargetsState>(() => targetsFrom(initialTargets));
  const [selectedOwner, setSelectedOwner] = useState<SCIPhysicalOwner | null>(null);
  const withTargets = initialTargets !== undefined;
  const [isDeleting, setIsDeleting] = useState(false);
  const [owners, setOwners] = useState<SCIPhysicalOwner[]>([]);
  const [ownersLoading, setOwnersLoading] = useState(false);
  const [ownersError, setOwnersError] = useState<string | null>(null);
  const jwt = useSelector((state: RootState) => state.auth.token);

  useEffect(() => {
    setContactForm(initialContact || {});
    setSelectedOwner(null);
  }, [initialContact, isOpen]);

  useEffect(() => {
    setTargetsForm(targetsFrom(initialTargets));
  }, [initialTargets?.targetedGestion, initialTargets?.targetedTransaction, isOpen]);

  const handleSelectOwner = (owner: SCIPhysicalOwner) => {
    setSelectedOwner(owner);
    setContactForm((f) => {
      const nom = owner.lastname || '';
      const prenom = ownerFirstnames(owner)[0] ?? '';
      // The registry has no gender: the civilité of another person must be chosen again
      const samePerson = (f.nom || '').trim().toLowerCase() === nom.toLowerCase() && (f.prenom || '').trim().toLowerCase() === prenom.toLowerCase();

      return { ...f, nom, prenom, civilite: samePerson ? f.civilite : '' };
    });
  };

  useEffect(() => {
    if (!isOpen || !siren || !jwt) {return;}
    setOwnersLoading(true);
    setOwnersError(null);
    fetch(`/api/scis/${siren}`, JWTHeader(jwt))
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((data: { owners?: SCIPhysicalOwner[] }) => {
        setOwners(Array.isArray(data?.owners) ? data.owners : []);
      })
      .catch(() => {
        setOwnersError('Impossible de charger les propriétaires physiques');
        setOwners([]);
      })
      .finally(() => setOwnersLoading(false));
  }, [isOpen, siren, jwt]);

  const changedTargets = (): SCIContactTargets | undefined => {
    if (!withTargets) {return undefined;}
    const initial = targetsFrom(initialTargets);
    const changed: SCIContactTargets = {};
    (['targetedGestion', 'targetedTransaction'] as const).forEach((key) => {
      const value = targetsForm[key];
      if (value !== null && value !== initial[key]) { changed[key] = value; }
    });

    return Object.keys(changed).length > 0 ? changed : undefined;
  };

  const handleSave = async() => {
    const saved = await onSave(contactForm, changedTargets());
    if (saved !== false) { onClose(); }
  };

  const handleDelete = async() => {
    if (!onDelete) {return;}
    try {
      setIsDeleting(true);
      await onDelete();
      setContactForm({});
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Popup isOpen={isOpen} onClose={onClose} title={<span>Qualifier le contact de la SCI</span>} additionalClass="w-full max-w-2xl">
      <div className="space-y-4 text-sm">
        <div className="font-semibold text-gray-700">{sciName}</div>
        <div className="space-y-2">
          <div className="flex items-start rounded border-l-4 border-yellow-500 bg-yellow-50 p-3">
            <ExclamationTriangleIcon className="mt-0.5 mr-2 h-5 w-5 text-yellow-600" />
            <p className="text-sm text-yellow-800">
              Ne contacter qu'un des représentants légaux de la SCI. <br/>
              Si votre contact n'est pas dans la liste, assurez-vous qu'il est bien un représentant légal et <b>n'utilisez pas d’autres contacts</b>.
            </p>
          </div>
          <div className="max-h-80 overflow-y-auto rounded border">
            {ownersLoading && (
              <div className="p-3">
                <Skeleton height={18} count={6} className="mb-2" />
              </div>
            )}
            {!ownersLoading && ownersError && (
              <div className="p-3 text-sm text-red-700">{ownersError}</div>
            )}
            {!ownersLoading && !ownersError && (
              <PhysicalOwnersTable owners={owners} onSelect={handleSelectOwner} selectedOwner={selectedOwner} />
            )}
          </div>
          <p className="text-xs text-gray-600">
            Choisissez le gérant dans la liste : son nom et son prénom sont repris, il ne reste qu'à saisir le téléphone et l'email.
          </p>
        </div>

        {isLoading && (
          <div className="flex items-center text-xs text-gray-600">
            <Spinner size="sm" className="mr-2" /> Chargement des personnes physiques de la SCI...
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-xs">Civilité</label>
            <select className="w-full rounded border px-2 py-1 disabled:bg-gray-100" value={contactForm.civilite || ''} onChange={(e) => setContactForm((f) => ({ ...f, civilite: e.target.value }))} disabled={isLoading || isSaving || ownersLoading}>
              <option value="">Sélectionner...</option>
              <option value="M.">M.</option>
              <option value="Mme">Mme</option>
              <option value="Mlle">Mlle</option>
              <option value="Dr">Dr</option>
              <option value="Me">Me</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs">Nom</label>
            <input className="w-full rounded border px-2 py-1 disabled:bg-gray-100" value={contactForm.nom || ''} onChange={(e) => setContactForm((f) => ({ ...f, nom: e.target.value }))} disabled={isLoading || isSaving || ownersLoading} />
          </div>
          <div>
            <label className="mb-1 block text-xs">Prénom</label>
            <input className="w-full rounded border px-2 py-1 disabled:bg-gray-100" value={contactForm.prenom || ''} onChange={(e) => setContactForm((f) => ({ ...f, prenom: e.target.value }))} disabled={isLoading || isSaving || ownersLoading} />
          </div>
          <div>
            <label className="mb-1 block text-xs">Téléphone</label>
            <input className="w-full rounded border px-2 py-1 disabled:bg-gray-100" value={contactForm.telephone || ''} onChange={(e) => setContactForm((f) => ({ ...f, telephone: e.target.value }))} disabled={isLoading || isSaving || ownersLoading} />
          </div>
          <div className="col-span-2">
            <label className="mb-1 block text-xs">Email</label>
            <input type="email" className="w-full rounded border px-2 py-1 disabled:bg-gray-100" value={contactForm.email || ''} onChange={(e) => setContactForm((f) => ({ ...f, email: e.target.value }))} disabled={isLoading || isSaving || ownersLoading} />
          </div>
          <div className="col-span-2">
            <label className="mb-1 block text-xs">Adresse <span className="text-gray-500">(facultatif)</span></label>
            <textarea className="w-full rounded border px-2 py-1 disabled:bg-gray-100" rows={3} value={contactForm.adresse || ''} onChange={(e) => setContactForm((f) => ({ ...f, adresse: e.target.value }))} disabled={isLoading || isSaving || ownersLoading} />
          </div>
        </div>
        {withTargets && (
          <div className="space-y-2 rounded border bg-gray-50 p-3">
            <div className="flex items-center justify-between gap-3">
              <span className="text-xs font-semibold text-gray-700">Cibler cette SCI</span>
              <button
                type="button"
                className="text-xs text-rgpd-blue underline disabled:opacity-50"
                disabled={isLoading || isSaving || ownersLoading}
                onClick={() => setTargetsForm({ targetedGestion: true, targetedTransaction: true })}
              >
                Oui pour les deux
              </button>
            </div>
            <TargetChoice
              label="Cible Gest Loc"
              value={targetsForm.targetedGestion}
              disabled={isLoading || isSaving || ownersLoading}
              onChange={(value) => setTargetsForm((t) => ({ ...t, targetedGestion: value }))}
            />
            <TargetChoice
              label="Cible Transac"
              value={targetsForm.targetedTransaction}
              disabled={isLoading || isSaving || ownersLoading}
              onChange={(value) => setTargetsForm((t) => ({ ...t, targetedTransaction: value }))}
            />
          </div>
        )}
        <div className="flex justify-between gap-2">
          <div>
            {onDelete && initialContact && (
              <button
                className="rounded border border-red-600 px-3 py-1 text-red-600 hover:bg-red-600 hover:text-white disabled:opacity-50"
                onClick={handleDelete}
                disabled={isDeleting || isLoading || isSaving || ownersLoading}
              >
                {isDeleting ? <><Spinner size="sm" className="mr-2" /> Suppression...</> : 'Supprimer le contact'}
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button className="rounded border px-3 py-1" onClick={onClose}>Annuler</button>
            <button
              className="rounded bg-rgpd-blue-dark px-3 py-1 text-white disabled:opacity-50"
              onClick={handleSave}
              disabled={isSaving || isLoading || ownersLoading}
            >
              {isSaving ? <><Spinner size="sm" className="mr-2" /> Enregistrement...</> : (isLoading ? <><Spinner size="sm" className="mr-2" /> Chargement...</> : 'Enregistrer')}
            </button>
          </div>
        </div>
      </div>
    </Popup>
  );
};

export default SCIContactQualificationModal;


